import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import DependentsGrid from '../components/DependentsGrid';
import LoadingSpinner from '../components/LoadingSpinner';
import { LuUsers, LuCircleCheck, LuPlus } from 'react-icons/lu';
import ErroLoadData from '../components/ErroLoadData';
import { dependenteAPI, formatters } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import CardGenerator from '../components/CardGenerator';

const DependentsContainer = styled.div`
  padding: 24px;
  max-width: 1200px;
  margin: 0 auto;

  @media (max-width: 768px) {
    padding: 16px 12px 24px;
  }
`;

const DependentsHeader = styled.div`
  margin-bottom: 32px;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;

  @media (max-width: 768px) {
    margin-bottom: 20px;
  }
`;

const Title = styled.h1`
  color: ${props => props.theme.colors.primary};
  font-size: 32px;
  font-weight: 700;
  margin-bottom: 8px;

  @media (max-width: 768px) {
    font-size: 24px;
  }
`;

const Subtitle = styled.p`
  color: ${props => props.theme.colors.grayMedium};
  font-size: 18px;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 15px;
  }
`;

const AddDependentPageButton = styled.button`
  background: ${props => props.theme.colors.gradientPrimary};
  color: ${props => props.theme.colors.white};
  border: none;
  padding: 14px 28px;
  border-radius: 12px;
  cursor: pointer;
  font-size: 16px;
  font-weight: 700;
  transition: all 0.3s ease;
  text-align: center;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin: 20px auto 0;
  box-shadow: 0 10px 15px -3px rgba(30, 64, 175, 0.25);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 15px 20px -3px rgba(30, 64, 175, 0.35);
  }

  @media (max-width: 480px) {
    width: 100%;
    max-width: 360px;
  }
`;

const StatsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 20px;
  margin-bottom: 32px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const StatCard = styled.div`
  background: ${props => props.theme.colors.white};
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 24px;
  display: flex;
  align-items: center;
  gap: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
  }

  @media (max-width: 768px) {
    padding: 15px 20px;
  }
`;

const StatIcon = styled.div`
  width: 54px;
  height: 54px;
  border-radius: 12px;
  background: ${props => props.bgColor || '#f1f5f9'};
  color: ${props => props.color || '#475569'};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  flex-shrink: 0;

  @media (max-width: 768px) {
    width: 44px;
    height: 44px;
    font-size: 20px;
  }
`;

const StatText = styled.div`
  display: flex;
  flex-direction: column;
`;

const StatNumber = styled.div`
  font-size: 32px;
  font-weight: 700;
  color: ${props => props.theme.colors.primary};
  line-height: 1;

  @media (max-width: 768px) {
    font-size: 24px;
  }
`;

const StatLabel = styled.div`
  font-size: 14px;
  color: ${props => props.theme.colors.grayMedium};
  font-weight: 500;
  margin-top: 4px;

  @media (max-width: 768px) {
    font-size: 12px;
    margin-top: 2px;
  }
`;

const ErrorMessage = styled.div`
  background: #fee2e2;
  border: 1px solid #fecaca;
  color: #dc2626;
  padding: 16px;
  border-radius: 8px;
  margin-bottom: 24px;
  text-align: center;
`;

const Dependents = () => {
  const [dependentes, setDependentes] = useState([]);
  const [stats, setStats] = useState({ total: 0, ativos: 0, inativos: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { user, selectedContrato } = useAuth();
  const numeroContrato = selectedContrato.numero;
  const [openAddNonce, setOpenAddNonce] = useState(0);

  const fetchDependentes = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await dependenteAPI.getAll();
      
      if (response.data.success) {
        // 3. Capture o número do contrato da resposta da API
        const { dependentes: deps, total, ativos, inativos, contrato } = response.data.data;
        setDependentes(deps);
        setStats({ total, ativos, inativos });
      } else {
        setError(response.data.message || 'Erro ao carregar dependentes');
      }
    } catch (err) {
      console.error('Erro ao buscar dependentes:', err);
      setError('Erro ao carregar dados dos dependentes. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDependentes();
  }, []);

  const calcularTotalMensal = () => {
    return dependentes
      .filter(d => d.status === 'ATIVO')
      .reduce((total, d) => total + (d.valorMensal || 0), 0);
  };

  if (loading) {
    return (
      <DependentsContainer>
        <LoadingSpinner />
      </DependentsContainer>
    );
  }

  if (error){
    return (
      <DependentsContainer>
        <ErroLoadData></ErroLoadData>
      </DependentsContainer>
    )
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <DependentsContainer>
      <DependentsHeader>
        <Title>Dependentes</Title>
        <Subtitle>
          Gerencie os dependentes do seu plano de assistência familiar
        </Subtitle>
        <AddDependentPageButton type="button" onClick={() => setOpenAddNonce((n) => n + 1)}>
          <LuPlus size={20} />
          Solicitar Inclusão de Dependente
        </AddDependentPageButton>
      </DependentsHeader>

      <StatsContainer>
        <StatCard>
          <StatIcon color="#1e3a8a" bgColor="#dbeafe">
            <LuUsers />
          </StatIcon>
          <StatText>
            <StatNumber>{stats.total}</StatNumber>
            <StatLabel>Total de Dependentes</StatLabel>
          </StatText>
        </StatCard>
                
        {/* <StatCard>
          <StatIcon color="#10b981" bgColor="#dcfce7">
            <LuCircleCheck />
          </StatIcon>
          <StatText>
            <StatNumber>{stats.ativos}</StatNumber>
            <StatLabel>Dependentes Ativos</StatLabel>
          </StatText>
        </StatCard> */}
      </StatsContainer>

      <DependentsGrid
        dependentes={dependentes}
        refetch={fetchDependentes}
        contrato={numeroContrato}
        showAddButton={false}
        openAddNonce={openAddNonce}
      />

      <div style={{ position: 'absolute', left: '-9999px', top: '-9999px' }}>
        <CardGenerator membros={[]} contrato={numeroContrato} />
      </div>

      </DependentsContainer>
    </motion.div>
  );
};

export default Dependents;
